import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { CornerDownRight, PercentIcon } from "lucide-react";
import { useEffect } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { z } from "zod";

import FormInput from "@/components/form-fields/form-input";
import { DragableObject } from "@/components/reusables/Dnd/DragableObject";
import { FormSelectSingle } from "@/components/reusables/custom-form-fields/FormSelect";
import { Input } from "@/components/ui/input";
import { HUNDRED } from "@/constants/constants";
import { ITEM_STATUS } from "@/constants/items.constant";
import { useGetItemsQuery } from "@/redux/api/itemQuery";
import { ITaskBreakDownSchema } from "@/schemas/task-and-claims";
import { InputKeyLimiter } from "@/utils/helper-functions/form.helper";

const PercentageChange = ({
  itemId,
  sectionId,
  customId,
}: {
  itemId: number;
  sectionId: number;
  customId: number;
}): React.JSX.Element => {
  const form = useFormContext<ITaskBreakDownSchema>();

  const singleData = form.watch(
    `data.${sectionId}.details.${itemId}.customDetails.${customId}`,
  );

  const changeCalculator = (): void => {
    const { newProgress, lastTaskProgress } = singleData;

    const prevProgress = form.getValues(
      `data.${sectionId}.details.${itemId}.customDetails.${customId}.change`,
    );
    const tempProgress = Number(
      (Number(newProgress) - Number(lastTaskProgress)).toFixed(2),
    );
    if (prevProgress !== tempProgress) {
      form.setValue(
        `data.${sectionId}.details.${itemId}.customDetails.${customId}.change`,
        Number(tempProgress),
        {
          shouldDirty: true,
        },
      );
    }
  };

  useEffect(() => {
    if (typeof itemId === "number" && singleData) {
      changeCalculator();
    }
  }, [JSON.stringify(singleData)]);

  return (
    <FormInput
      form={form}
      name={`data.${sectionId}.details.${itemId}.customDetails.${customId}.change`}
      className='w-full'
      input={{
        type: "number",
        step: 0.01,
        disabled: true,
        placeholder: "Change",
        required: true,
      }}
    />
  );
};

const TaskBdCustomField = ({
  sectionId,
  ItemId,
}: {
  sectionId: number;
  ItemId: number;
}): React.JSX.Element => {
  const form = useFormContext<ITaskBreakDownSchema>();
  const mainArray = useFieldArray({
    control: form.control,
    name: `data.${sectionId}.details.${ItemId}.customDetails`,
    keyName: "customFieldId",
  });

  const { data: itemList } = useGetItemsQuery({
    filter: "?type=ESTIMATION",
  });
  const { fields: CustomChildFields, move } = mainArray;

  const handleReorder = (e: DragEndEvent): void => {
    if (!e.over) {
      return;
    }

    const oldIndex = CustomChildFields.findIndex(
      (field) => field.customFieldId === e.active.id,
    );
    const newIndex = CustomChildFields.findIndex(
      (field) => field.customFieldId === e.over!.id,
    );

    if (oldIndex !== newIndex) {
      move(oldIndex, newIndex);
      const data = form.getValues(
        `data.${sectionId}.details.${ItemId}.customDetails`,
      );

      // Update positions after reordering
      const updatedFields = data?.map((field, index) => ({
        ...field,
        position: index,
      }));

      form.setValue(
        `data.${sectionId}.details.${ItemId}.customDetails`,
        updatedFields,
      );
    }
  };

  return (
    <DndContext onDragEnd={handleReorder}>
      <div>
        <SortableContext
          items={CustomChildFields?.map((field) => field.customFieldId)}
        >
          {CustomChildFields?.map((field, customId) => (
            <DragableObject
              key={`data-${sectionId}-${ItemId}-${field.customFieldId}`}
              id={field.customFieldId}
            >
              <div className='group grid w-full grid-cols-[2fr_2.5fr_1fr_1fr_1fr_1fr_1.1fr_0.4fr] rounded-md gap-4 items-start '>
                {z.string().uuid().safeParse(field.item).success ? (
                  <div className='flex items-center w-full'>
                    <CornerDownRight className='w-5' color='blue' />
                    <FormInput
                      form={form}
                      name={`data.${sectionId}.details.${ItemId}.customDetails.${customId}.item`}
                      className='w-full'
                      render={(field) => (
                        <FormSelectSingle
                          disabled
                          placeholder='Please Select Item'
                          field={field}
                          className='w-full'
                          options={
                            itemList?.data
                              ?.filter(
                                (data) => data?.status === ITEM_STATUS.ACTIVE,
                              )
                              .map((item) => ({
                                label: item.title || "",
                                value: item.id,
                              })) || []
                          }
                          onFieldValueChange={(value) => {
                            const item = itemList?.data?.find(
                              (item) => item.id === value,
                            );
                            form.setValue(
                              `data.${sectionId}.details.${ItemId}.customDetails.${customId}.item`,
                              item?.id || "",
                            );
                            form.setValue(
                              `data.${sectionId}.details.${ItemId}.customDetails.${customId}.description`,
                              item?.description || "",
                            );

                            form.setValue(
                              `data.${sectionId}.details.${ItemId}.customDetails.${customId}.unit`,
                              item?.unit || "unit",
                            );
                          }}
                        />
                      )}
                    />
                  </div>
                ) : (
                  <div className='flex items-center w-full'>
                    <CornerDownRight className='w-5' color='blue' />
                    <FormInput
                      form={form}
                      name={`data.${sectionId}.details.${ItemId}.customDetails.${customId}.item`}
                      className='w-full'
                      input={{
                        type: "text",
                        disabled: true,
                        placeholder: "Enter Item",
                      }}
                    />
                  </div>
                )}
                <FormInput
                  form={form}
                  name={`data.${sectionId}.details.${ItemId}.customDetails.${customId}.description`}
                  className='w-full'
                  input={{
                    type: "text",
                    disabled: true,
                    placeholder: "Description",
                  }}
                />
                <FormInput
                  form={form}
                  name={`data.${sectionId}.details.${ItemId}.customDetails.${customId}.unit`}
                  className='w-full'
                  input={{
                    type: "text",
                    disabled: true,
                    placeholder: "Unit",
                  }}
                />
                <FormInput
                  form={form}
                  name={`data.${sectionId}.details.${ItemId}.customDetails.${customId}.quantity`}
                  className='w-full'
                  input={{
                    type: "text",
                    disabled: true,
                    placeholder: "Quantity",
                  }}
                />
                <FormInput
                  form={form}
                  name={`data.${sectionId}.details.${ItemId}.customDetails.${customId}.lastTaskProgress`}
                  render={(field) => (
                    <Input
                      disabled
                      placeholder={`0`}
                      className='w-full'
                      type='number'
                      icon={{
                        icon: PercentIcon,
                        position: "right",
                      }}
                      {...field}
                    />
                  )}
                />{" "}
                <FormInput
                  form={form}
                  name={`data.${sectionId}.details.${ItemId}.customDetails.${customId}.newProgress`}
                  render={(field) => (
                    <Input
                      className='w-full border-primary '
                      icon={{
                        icon: PercentIcon,
                        position: "right",
                      }}
                      step={0.01}
                      onKeyDown={(e) =>
                        InputKeyLimiter({ event: e, maxValue: HUNDRED })
                      }
                      {...field}
                    />
                  )}
                />
                <PercentageChange
                  sectionId={sectionId}
                  itemId={ItemId}
                  customId={customId}
                />
              </div>
            </DragableObject>
          ))}
        </SortableContext>
      </div>
    </DndContext>
  );
};

export default TaskBdCustomField;
